import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBooking } from '../../context/BookingContext'
import { appointmentsAPI, couponsAPI } from '../../api'
import { format } from 'date-fns'
import {
  RiArrowLeftLine, RiBankCardLine, RiStoreLine,
  RiCheckLine, RiLoader4Line, RiTicketLine,
} from 'react-icons/ri'
import toast from 'react-hot-toast'

export default function PaymentStep() {
  const navigate = useNavigate()
  const {
    booking, prevStep,
    setCoupon, setPayMethod, setAppointmentId,
    total, subtotal, discount, reset,
  } = useBooking()

  const [loading,     setLoading]     = useState(false)
  const [couponInput, setCouponInput] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)

  const applyCoupon = async () => {
    if (!couponInput.trim()) return
    setCouponLoading(true)
    try {
      const { data } = await couponsAPI.validate(couponInput.trim())
      setCoupon(data.data)
      toast.success('Coupon applied!')
    } catch {
      toast.error('Invalid or expired coupon code')
    } finally {
      setCouponLoading(false)
    }
  }

  const handleBooking = async () => {
    setLoading(true)
    try {
      const payload = {
        services:       booking.services.map((s) => s.id),
        staff_id:       booking.staff?.id !== 'any' ? booking.staff?.id : null,
        date:           format(booking.date, 'yyyy-MM-dd'),
        time:           booking.timeSlot,
        customer_name:  booking.customer.name,
        customer_email: booking.customer.email,
        customer_phone: booking.customer.phone,
        notes:          booking.customer.notes,
        payment_method: booking.paymentMethod,
        coupon_code:    booking.coupon?.code,
      }

      const { data } = await appointmentsAPI.create(payload)
      setAppointmentId(data.data.id)

      if (booking.paymentMethod === 'online' && data.data.payment_url) {
        window.location.href = data.data.payment_url
      } else {
        navigate(`/booking/confirmation/${data.data.id}`)
        reset()
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Booking failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h2 className="font-display text-salon-dark text-2xl font-bold mb-1">Payment & Confirm</h2>
      <p className="text-salon-muted font-body text-sm mb-6">Review your booking and complete payment.</p>

      {/* Coupon */}
      <div className="mb-6">
        <label className="input-label">Coupon / Promo Code</label>
        <div className="flex gap-2">
          <input
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            className="input-field flex-1"
            placeholder="Enter coupon code"
            disabled={!!booking.coupon}
          />
          {booking.coupon ? (
            <button onClick={() => { setCoupon(null); setCouponInput('') }}
              className="px-4 py-3 rounded-xl bg-red-50 text-red-500 font-body font-semibold text-sm hover:bg-red-100 transition-colors">
              Remove
            </button>
          ) : (
            <button onClick={applyCoupon} disabled={couponLoading}
              className="px-4 py-3 rounded-xl bg-salon-primary text-white font-body font-semibold text-sm
                         hover:bg-salon-primary-light transition-colors disabled:opacity-60">
              {couponLoading ? <RiLoader4Line className="animate-spin" /> : 'Apply'}
            </button>
          )}
        </div>
        {booking.coupon && (
          <p className="text-green-600 text-xs font-body mt-1 flex items-center gap-1">
            <RiCheckLine /> "{booking.coupon.code}" applied – saving ₹{discount.toLocaleString()}
          </p>
        )}
      </div>

      {/* Price breakdown */}
      <div className="rounded-2xl bg-salon-cream border border-salon-cream-dark p-5 mb-6 space-y-2">
        {booking.services.map((s) => (
          <div key={s.id} className="flex justify-between text-sm font-body">
            <span className="text-salon-dark">{s.name}</span>
            <span className="text-salon-dark font-medium">₹{parseFloat(s.price).toLocaleString()}</span>
          </div>
        ))}
        {discount > 0 && (
          <div className="flex justify-between text-sm font-body text-green-600 border-t border-salon-cream-dark pt-2 mt-2">
            <span>Discount</span>
            <span>– ₹{discount.toLocaleString()}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-bold font-body border-t border-salon-cream-dark pt-3 mt-2 text-salon-dark">
          <span>Total</span>
          <span>₹{total.toLocaleString()}</span>
        </div>
      </div>

      {/* Payment method */}
      <div className="mb-8">
        <p className="input-label mb-3">Payment Method</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'online', icon: RiBankCardLine, label: 'Pay Online', sub: 'Card / UPI / Netbanking' },
            { id: 'at_salon', icon: RiStoreLine, label: 'Pay at Salon', sub: 'Cash / Card on arrival' },
          ].map((m) => (
            <button key={m.id}
              onClick={() => setPayMethod(m.id)}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${
                booking.paymentMethod === m.id
                  ? 'border-salon-primary bg-salon-primary/5'
                  : 'border-salon-cream-dark bg-white hover:border-salon-primary/40'
              }`}
            >
              <m.icon className="text-salon-primary text-xl mb-2" />
              <p className="font-semibold font-body text-salon-dark text-sm">{m.label}</p>
              <p className="text-salon-muted text-xs font-body mt-0.5">{m.sub}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={prevStep} className="btn-ghost"><RiArrowLeftLine /> Back</button>
        <button onClick={handleBooking} disabled={loading} className="btn-primary min-w-[160px]">
          {loading
            ? <><RiLoader4Line className="animate-spin" /> Processing...</>
            : <><RiCheckLine /> Confirm Booking</>
          }
        </button>
      </div>
    </div>
  )
}
